import { localCalendarDateString } from '../../utils/localDate';

// Shared helpers for the order form (create / edit / view).
// Extracted from the old 3-step wizard so OrderDetail can reuse the same
// beam-plan and stage-context plumbing regardless of mode.
//
// Entity ids are UUID strings from the backend (JSON). Keep them as strings in
// state and request bodies — never Number(id). Optional FKs: send null not "".

export { resolveFileUrl, API_BASE_URL } from '../../api/mediaUrl';

function blankToNull(value) {
  const s = String(value ?? '').trim();
  return s || null;
}

// ---------- Stage context (vendor sub-stage details) ----------
export const STAGE_CONTEXT_EMPTY = {
  buttaCuttingPartyId: '',
  buttaCuttingBpcs: '',
  millFinishPartyId: '',
  millFinishBpcs: '',
  borderCuttingPartyId: '',
  borderCuttingBpcs: '',
  handworkPartyId: '',
  handworkBpcs: '',
};

export const WORKFLOW_SHOWS_BUTTA_CUTTING = new Set(['BUTTA_CUTTING']);
export const WORKFLOW_SHOWS_MILL_FINISH = new Set(['MILL_FINISH']);
export const WORKFLOW_SHOWS_BORDER_CUTTING = new Set(['BORDER_CUTTING']);
export const WORKFLOW_SHOWS_HANDWORK = new Set(['HANDWORK']);

export const normalizeStageContextFromApi = (order) => {
  const sc = order?.stageContext;
  if (!sc) return { ...STAGE_CONTEXT_EMPTY };
  return {
    buttaCuttingPartyId: sc.buttaCuttingPartyId ?? '',
    buttaCuttingBpcs: sc.buttaCuttingBpcs != null ? String(sc.buttaCuttingBpcs) : '',
    millFinishPartyId: sc.millFinishPartyId ?? '',
    millFinishBpcs: sc.millFinishBpcs != null ? String(sc.millFinishBpcs) : '',
    borderCuttingPartyId: sc.borderCuttingPartyId ?? '',
    borderCuttingBpcs: sc.borderCuttingBpcs != null ? String(sc.borderCuttingBpcs) : '',
    handworkPartyId: sc.handworkPartyId ?? '',
    handworkBpcs: sc.handworkBpcs != null ? String(sc.handworkBpcs) : '',
  };
};

const toIntOrNull = (v) => {
  const n = Number.parseInt(String(v ?? '').trim(), 10);
  return Number.isFinite(n) ? n : null;
};

export const mapStageContextForApi = (ctx) => {
  const c = { ...STAGE_CONTEXT_EMPTY, ...(ctx || {}) };
  const dto = {
    buttaCuttingPartyId: blankToNull(c.buttaCuttingPartyId),
    buttaCuttingBpcs: toIntOrNull(c.buttaCuttingBpcs),
    millFinishPartyId: blankToNull(c.millFinishPartyId),
    millFinishBpcs: toIntOrNull(c.millFinishBpcs),
    borderCuttingPartyId: blankToNull(c.borderCuttingPartyId),
    borderCuttingBpcs: toIntOrNull(c.borderCuttingBpcs),
    handworkPartyId: blankToNull(c.handworkPartyId),
    handworkBpcs: toIntOrNull(c.handworkBpcs),
  };
  const hasAny =
    dto.buttaCuttingPartyId != null ||
    dto.millFinishPartyId != null ||
    dto.borderCuttingPartyId != null ||
    dto.handworkPartyId != null ||
    dto.buttaCuttingBpcs != null ||
    dto.millFinishBpcs != null ||
    dto.borderCuttingBpcs != null ||
    dto.handworkBpcs != null;
  return hasAny ? dto : null;
};

// ---------- Misc ----------
// resolveFileUrl re-exported at top of file from api/mediaUrl

export const formatDisplayDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

// ---------- Beam plan ----------
export const DEFAULT_BEAM_ROW_QTY = { pieces: '0', unit: '', takaNo: '', rPcs: '0' };

export const sortDesignFeeders = (feeders) =>
  [...(feeders || [])].sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));

export const feederHeaderShort = (name) => {
  const n = String(name || '').trim();
  if (!n) return '—';
  if (n.length <= 14) return n;
  return `${n.slice(0, 12)}…`;
};

/** Design "Add picks" copied to order.pick — not average picks. */
export const resolveDesignPickForOrder = (designId, designs) => {
  if (!designId) return '';
  const design = (designs || []).find((d) => String(d.id) === String(designId));
  if (design?.addPicks == null || String(design.addPicks).trim() === '') return '';
  return String(design.addPicks).trim();
};

/** True when the form's selected design is marked Sample. */
export const isSampleDesignSelected = (form, designs = []) => {
  const id = form?.designId;
  if (!id) return false;
  const design = (designs || []).find((d) => String(d.id) === String(id));
  return design?.sampleDesign === true;
};

/** Stored order pick (from design add picks); fall back to design add picks if unset. */
export const formatOrderPickDisplay = (order, designs) => {
  const pickStr = order?.pick != null ? String(order.pick).trim() : '';
  if (pickStr !== '' && pickStr !== '0') {
    return pickStr;
  }
  const design = (designs || []).find((d) => String(d.id) === String(order?.designId));
  if (design?.addPicks != null && String(design.addPicks).trim() !== '') {
    return String(design.addPicks).trim();
  }
  return pickStr !== '' ? pickStr : '—';
};

/** Design read count for order summary (from linked design). */
export const formatOrderReadDisplay = (order, designs) => {
  const design = (designs || []).find((d) => String(d.id) === String(order?.designId));
  if (design?.readCount != null && String(design.readCount).trim() !== '') {
    return String(design.readCount);
  }
  return '—';
};

export const mergeBeamRowsWithLineItems = (beams, lineItems) => {
  if (!beams?.length) return beams;
  const lines = Array.isArray(lineItems) ? lineItems : [];
  return beams.map((b, i) => {
    const li = lines[i];
    if (!li) return { ...DEFAULT_BEAM_ROW_QTY, ...b };
    const linePcs = li.pieces != null ? Number.parseInt(String(li.pieces), 10) : NaN;
    const planPcs = Number.parseInt(String(b.pieces ?? '0').trim(), 10) || 0;
    /** Beam-plan PCS is what the user edits; line items may still be the draft placeholder (0). */
    const mergedPcs = planPcs > 0
      ? planPcs
      : (Number.isFinite(linePcs) && linePcs > 0 ? linePcs : 0);
    const planR = Number.parseInt(String(b.rPcs ?? '0').trim(), 10) || 0;
    const lineR = li.rPcs != null ? Number.parseInt(String(li.rPcs), 10) : NaN;
    const mergedR = planR > 0 ? planR : (Number.isFinite(lineR) ? lineR : 0);
    const planTaka = String(b.takaNo ?? '').trim();
    const lineTaka = li.takaNo != null ? String(li.takaNo).trim() : '';
    return {
      ...b,
      pieces: String(mergedPcs),
      /** Machine/unit is assigned at beam start — not entered on the order form. */
      unit: '',
      takaNo: planTaka || lineTaka,
      rPcs: String(mergedR),
    };
  });
};

export const beamPiecesTotal = (beams) =>
  (beams || []).reduce((s, b) => s + (Number.parseInt(String(b?.pieces ?? '0'), 10) || 0), 0);

export const isBeamRowFullyEmpty = (beam) => {
  if (!beam) return true;
  if (String(beam.beamColorId || '').trim()) return false;
  const feeders = beam.feeders || [];
  if (feeders.some((f) => String(f.colorId || '').trim())) return false;
  const pcs = Number.parseInt(String(beam.pieces ?? '').trim(), 10);
  if (Number.isFinite(pcs) && pcs > 0) return false;
  const pcsRaw = String(beam.pieces ?? '').trim();
  if (pcsRaw !== '' && pcsRaw !== '0') return false;
  if (String(beam.unit || '').trim()) return false;
  if (String(beam.takaNo || '').trim()) return false;
  const r = Number.parseInt(String(beam.rPcs ?? '0').trim(), 10);
  if (Number.isFinite(r) && r > 0) return false;
  return true;
};

export const pruneTrailingEmptyBeams = (beams) => {
  const out = [...(beams || [])];
  while (out.length > 0 && isBeamRowFullyEmpty(out[out.length - 1])) {
    out.pop();
  }
  return out;
};

export const withBeamRowDefaults = (b) => ({
  ...b,
  unit: String(b.unit ?? '').trim(),
  takaNo: (b.takaNo || '').trim(),
  rPcs: String(Math.max(0, Number.parseInt(String(b.rPcs ?? '0'), 10) || 0)),
});

export const buildDefaultBeamPlanFromFeeders = (feeders) => {
  const sorted = sortDesignFeeders(feeders);
  return {
    beams: [
      {
        ...DEFAULT_BEAM_ROW_QTY,
        beamColorId: '',
        beamColorName: '',
        beamColorCode: '',
        feeders: sorted.map((f) => ({
          designFeederId: f.id ?? '',
          feederId: f.feederId ?? '',
          feederName: f.feederName || '',
          colorId: '',
          colorName: '',
          colorCode: '',
        })),
      },
    ],
  };
};

/** Align saved feeder colors with the design's feeder list (view + reload after draft save). */
export const mergeBeamFeedersWithDesign = (savedFeeders, designFeeders) => {
  const sorted = sortDesignFeeders(designFeeders);
  if (!sorted.length) {
    return (savedFeeders || []).map((f) => ({
      designFeederId: f.designFeederId ?? '',
      feederId: f.feederId ?? '',
      feederName: f.feederName || '',
      colorId: f.colorId ?? '',
      colorName: f.colorName || '',
      colorCode: f.colorCode || '',
    }));
  }
  return sorted.map((df, idx) => {
    // Match by design-feeder row id only — feederId repeats across F1/F2/F3/F4 slots.
    const saved = (savedFeeders || []).find(
      (f) => f.designFeederId && df.id && String(f.designFeederId) === String(df.id)
    ) || (savedFeeders || [])[idx];
    return {
      designFeederId: df.id ?? '',
      feederId: df.feederId ?? '',
      feederName: df.feederName || '',
      colorId: saved?.colorId ?? '',
      colorName: saved?.colorName || '',
      colorCode: saved?.colorCode || '',
    };
  });
};

export const normalizeBeamPlanFromApi = (apiPlan, designId, designList) => {
  const design = (designList || []).find((d) => String(d.id) === String(designId));
  const designFeeders = Array.isArray(design?.feeders) ? design.feeders : [];

  if (apiPlan?.beams?.length) {
    const planPieces = (b) => {
      const n = Number.parseInt(String(b.pieces ?? ''), 10);
      return Number.isFinite(n) ? String(n) : DEFAULT_BEAM_ROW_QTY.pieces;
    };
    return {
      beams: apiPlan.beams.map((b) => ({
        ...DEFAULT_BEAM_ROW_QTY,
        beamColorId: b.beamColorId ?? '',
        beamColorName: b.beamColorName || '',
        beamColorCode: b.beamColorCode || '',
        pieces: planPieces(b),
        unit: DEFAULT_BEAM_ROW_QTY.unit,
        takaNo: b.takaNo != null ? String(b.takaNo) : DEFAULT_BEAM_ROW_QTY.takaNo,
        rPcs: b.rPcs != null ? String(b.rPcs) : DEFAULT_BEAM_ROW_QTY.rPcs,
        feeders: mergeBeamFeedersWithDesign(b.feeders, designFeeders),
      })),
    };
  }
  return buildDefaultBeamPlanFromFeeders(designFeeders);
};

/** Keep one design skeleton row on draft save when colors/pcs are still empty. */
export const resolveBeamsForPersist = (beams, designId, designList) => {
  const pruned = pruneTrailingEmptyBeams(beams || []);
  if (pruned.length > 0) return pruned;
  const design = (designList || []).find((d) => String(d.id) === String(designId));
  const designFeeders = Array.isArray(design?.feeders) ? design.feeders : [];
  if (designFeeders.length > 0) {
    return buildDefaultBeamPlanFromFeeders(designFeeders).beams;
  }
  return pruned;
};

export const mapBeamPlanForApi = (beamPlan) => {
  if (!beamPlan?.beams?.length) return null;
  return {
    beams: beamPlan.beams.map((beam) => ({
      beamColorId: blankToNull(beam.beamColorId),
      beamColorName: beam.beamColorName?.trim() || null,
      beamColorCode: beam.beamColorCode?.trim() || null,
      /** Must match backend OrderBeamAssignmentDto.pieces — used to seed order_beams.planned_pieces. */
      pieces: Math.max(0, Number.parseInt(String(beam.pieces ?? '0'), 10) || 0),
      takaNo: (beam.takaNo || '').trim() || null,
      rPcs: Math.max(0, Number.parseInt(String(beam.rPcs ?? '0'), 10) || 0),
      feeders: (beam.feeders || []).map((f) => ({
        designFeederId: blankToNull(f.designFeederId),
        feederId: blankToNull(f.feederId),
        feederName: f.feederName?.trim() || null,
        colorId: blankToNull(f.colorId),
        colorName: f.colorName?.trim() || null,
        colorCode: f.colorCode?.trim() || null,
      })),
    })),
  };
};

/** True when a DRAFT has no party and no design selected yet. */
export const isEmptyDraftOrder = (order) => {
  if (!order) return true;
  const hasDesign = order.designId != null && String(order.designId).trim() !== '';
  const hasParty = order.partyId != null && String(order.partyId).trim() !== '';
  return !hasDesign && !hasParty;
};

// ---------- Form state ----------
export const buildEmptyOrderForm = () => ({
  partyId: '',
  deliveryDetailId: '',
  khataId: '',
  khataName: '',
  transportPartyId: '',
  transportServiceName: '',
  brokerId: '',
  brokerName: '',
  designId: '',
  remark: '',
  price: '',
  dispatchInvoiceImage: '',
  dispatchLrImage: '',
  orderDate: localCalendarDateString(),
  workflowStatus: 'DRAFT',
  stageContext: { ...STAGE_CONTEXT_EMPTY },
  beamPlan: null,
  beamPlanSourceDesignId: null,
});

/** Hydrate a form from a loaded order response. `designs` is used to seed
 * the default beam plan if the order has none yet. */
export const mapOrderToForm = (order, designs) => {
  if (!order) return buildEmptyOrderForm();
  const normalized = normalizeBeamPlanFromApi(order.beamPlan, order.designId, designs);
  const beamsMerged = mergeBeamRowsWithLineItems(normalized.beams, order.lineItems);
  return {
    partyId: order.partyId ?? '',
    deliveryDetailId: order.deliveryDetailId ?? '',
    khataId: order.khataId ?? '',
    khataName: order.khataName || '',
    transportPartyId: order.transportPartyId ?? '',
    transportServiceName: order.transportServiceName || '',
    brokerId: order.brokerId ?? '',
    brokerName: order.brokerName || '',
    designId: order.designId ?? '',
    remark: order.remark || '',
    price: order.price != null && order.price !== '' ? String(order.price) : '',
    dispatchInvoiceImage: order.dispatchInvoiceImage || '',
    dispatchLrImage: order.dispatchLrImage || '',
    orderDate: order.orderDate || localCalendarDateString(),
    workflowStatus: order.workflowStatus || 'DRAFT',
    stageContext: normalizeStageContextFromApi(order),
    beamPlan: { beams: beamsMerged },
    beamPlanSourceDesignId: order.designId ?? null,
  };
};

export const buildOrderPayload = (form, designs = []) => {
  const rawBeams = form.beamPlan?.beams ?? [];
  const beams = rawBeams.map(withBeamRowDefaults);
  const lineItems = beams.map((b) => ({
      pieces: Math.max(0, Number.parseInt(String(b.pieces ?? '0'), 10) || 0),
      unit: null,
      rPcs: Math.max(0, Number.parseInt(String(b.rPcs ?? '0'), 10) || 0),
      takaNo: (b.takaNo || '').trim(),
      status: 'Pending',
    }));
  const totalPieces = lineItems.reduce((s, r) => s + r.pieces, 0);
  return {
    partyId: blankToNull(form.partyId),
    deliveryDetailId: blankToNull(form.deliveryDetailId),
    khataId: blankToNull(form.khataId),
    transportPartyId: blankToNull(form.transportPartyId),
    brokerId: blankToNull(form.brokerId),
    designId: blankToNull(form.designId),
    pick: resolveDesignPickForOrder(form.designId, designs),
    totalPieces,
    remark: form.remark || '',
    price: (() => {
      const n = Number.parseFloat(String(form.price ?? '').trim());
      if (!Number.isFinite(n) || n <= 0) return null;
      return n;
    })(),
    orderDate: form.orderDate,
    beamColor: '',
    beamPlan: mapBeamPlanForApi({ beams }),
    lineItems,
    workflowStatus: form.workflowStatus,
    stageContext: mapStageContextForApi(form.stageContext),
  };
};

/** Partial payload for DRAFT checkpoint saves — empty fields are sent as null and skipped server-side. */
export const buildDraftOrderPayload = (form, designs = []) => {
  const full = buildOrderPayload(form, designs);
  return {
    partyId: full.partyId,
    deliveryDetailId: full.deliveryDetailId,
    khataId: full.khataId,
    transportPartyId: full.transportPartyId,
    brokerId: full.brokerId,
    designId: full.designId,
    pick: full.designId ? full.pick : null,
    totalPieces: full.totalPieces,
    remark: full.remark,
    price: full.price,
    orderDate: full.orderDate,
    beamColor: full.beamColor,
    beamPlan: full.beamPlan,
    lineItems: full.lineItems,
    workflowStatus: full.workflowStatus,
    stageContext: full.stageContext,
  };
};

export const buildOperationalDraftPayload = (form, designs = []) => {
  const operational = buildOperationalOrderPayload(form, designs);
  return {
    designId: operational.designId,
    khataId: operational.khataId,
    pick: operational.pick,
    totalPieces: operational.totalPieces,
    remark: operational.remark,
    orderDate: operational.orderDate,
    beamColor: operational.beamColor,
    beamPlan: operational.beamPlan,
    lineItems: operational.lineItems,
    workflowStatus: operational.workflowStatus,
    stageContext: operational.stageContext,
  };
};

/** Stable JSON fingerprint for dirty-checking draft form state. */
export const draftFormFingerprint = (form, { commercial = true, designs = [] } = {}) => {
  const prunedBeams = resolveBeamsForPersist(form.beamPlan?.beams || [], form.designId, designs);
  const withBeams = { ...form, beamPlan: { beams: prunedBeams } };
  const payload = commercial
    ? buildDraftOrderPayload(withBeams, designs)
    : buildOperationalDraftPayload(withBeams, designs);
  return JSON.stringify(payload);
};

export const validateOrderForm = (form, designs = []) => {
  const sample = isSampleDesignSelected(form, designs);
  if (!sample) {
    const rawPrice = String(form.price ?? '').trim();
    if (!rawPrice) return 'Please enter the order price';
    const priceNum = Number.parseFloat(rawPrice);
    if (!Number.isFinite(priceNum) || priceNum <= 0) return 'Please enter a valid order price (greater than zero)';
    if (!String(form.partyId || '').trim()) return 'Please select a party';
    if (!String(form.transportPartyId || '').trim()) return 'Please select a transport service (transporter)';
    if (!String(form.brokerId || '').trim()) return 'Please select a broker';
  } else {
    const rawPrice = String(form.price ?? '').trim();
    if (rawPrice) {
      const priceNum = Number.parseFloat(rawPrice);
      if (!Number.isFinite(priceNum) || priceNum <= 0) {
        return 'Please enter a valid order price (greater than zero), or leave price blank';
      }
    }
  }
  if (!String(form.khataId || '').trim()) return 'Please select a production unit';
  if (!String(form.designId || '').trim()) return 'Please select a design';
  const beams = pruneTrailingEmptyBeams(form.beamPlan?.beams || []);
  if (beams.length === 0) return 'Add at least one beam row';
  const pcs = Number.parseInt(String(beams[0].pieces ?? '').trim(), 10);
  if (!Number.isFinite(pcs) || pcs < 1) return 'Beam 1: enter PCS (at least 1)';
  return '';
};

/** Draft save for users with EDIT but without commercial data access. */
export const validateOrderFormOperational = (form) => {
  if (!String(form.khataId || '').trim()) return 'Please select a production unit';
  if (!String(form.designId || '').trim()) return 'Please select a design';
  const beams = pruneTrailingEmptyBeams(form.beamPlan?.beams || []);
  if (beams.length === 0) return 'Add at least one beam row';
  const pcs = Number.parseInt(String(beams[0].pieces ?? '').trim(), 10);
  if (!Number.isFinite(pcs) || pcs < 1) return 'Beam 1: enter PCS (at least 1)';
  return '';
};

export const buildOperationalOrderPayload = (form, designs = []) => {
  const full = buildOrderPayload(form, designs);
  return {
    designId: full.designId,
    khataId: full.khataId,
    pick: full.pick,
    totalPieces: full.totalPieces,
    remark: full.remark,
    orderDate: full.orderDate,
    beamColor: full.beamColor,
    beamPlan: full.beamPlan,
    lineItems: full.lineItems,
    workflowStatus: full.workflowStatus,
    stageContext: full.stageContext,
  };
};

/** Resolves planned pieces for a beam row (matches backend approve / seed logic). */
/** Prefer saved line-item PCS over beam-plan JSON (avoids stale plan counts). */
export const resolveBeamPiecesForApprove = (beam, beamIndexZeroBased, order) => {
  if (Array.isArray(order?.lineItems) && order.lineItems[beamIndexZeroBased]) {
    const lp = order.lineItems[beamIndexZeroBased].pieces;
    if (lp != null && Number.parseInt(String(lp), 10) > 0) {
      return Number.parseInt(String(lp), 10) || 0;
    }
  }
  return Number.parseInt(String(beam?.pieces ?? '0').trim(), 10) || 0;
};

const beamRowHasBeamColorForApprove = (beam) => {
  if (beam?.beamColorId != null && String(beam.beamColorId).trim() !== '') return true;
  if (beam?.beamColorName && String(beam.beamColorName).trim()) return true;
  return false;
};

const feederCellHasColorForApprove = (f) => {
  if (!f) return false;
  if (f.colorId != null && String(f.colorId).trim() !== '') return true;
  if (f.colorName && String(f.colorName).trim()) return true;
  return false;
};

const beamRowHasAtLeastOneFeederColorForApprove = (beam) => {
  const feeders = beam?.feeders;
  if (!Array.isArray(feeders) || feeders.length === 0) return false;
  return feeders.some(feederCellHasColorForApprove);
};

/**
 * Rules aligned with backend approve: multi-beam → beam colour per active row; at least one feeder colour
 * per active row when the design has feeders; single-beam → design beam name only (no beam colour).
 */
export const validateOrderApproveBeamPlan = (order, design) => {
  if (!order) return 'Order not loaded.';
  if (!design) {
    return 'Design data not loaded. Refresh the page and try again.';
  }
  const singleBeam = design.singleBeam === true;
  if (singleBeam && !String(design.beamName || '').trim()) {
    return 'This single-beam design has no beam name. Edit the design, then try again.';
  }

  const designHasFeeders = Array.isArray(design.feeders) && design.feeders.length > 0;
  const beams = order.beamPlan?.beams;

  if (!beams?.length) {
    if (designHasFeeders) {
      return 'Complete the beam and feeder plan (including at least one feeder colour per active beam) before approving.';
    }
    if (!singleBeam && !String(order.beamColor || '').trim()) {
      return 'Select a beam colour before approving.';
    }
    return '';
  }

  let anyActive = false;
  for (let i = 0; i < beams.length; i += 1) {
    const beam = beams[i];
    const pcs = resolveBeamPiecesForApprove(beam, i, order);
    if (pcs <= 0) continue;
    anyActive = true;
    if (!singleBeam && !beamRowHasBeamColorForApprove(beam)) {
      return `Beam ${i + 1}: select a beam colour before approving.`;
    }
    if (designHasFeeders && !beamRowHasAtLeastOneFeederColorForApprove(beam)) {
      return `Beam ${i + 1}: select at least one feeder colour before approving (other feeders may stay empty).`;
    }
  }

  if (!anyActive) {
    return 'Enter a positive piece count on at least one beam before approving.';
  }
  return '';
};

/** Started machine lines (RUNNING / ON_HOLD / COMPLETED) with a machine number — PDF export eligible. */
export const isBeamPdfExportable = (runtimeBeam) => {
  if (!runtimeBeam || typeof runtimeBeam !== 'object') return false;
  const status = String(runtimeBeam.status ?? '').trim().toUpperCase();
  if (!status || status === 'PENDING') return false;
  const machine = String(runtimeBeam.machineNo ?? runtimeBeam.machine_no ?? '').trim();
  return machine.length > 0;
};

/** Display order code with leading # (matches Orders list). */
export const formatOrderCode = (code) => {
  const raw = String(code || '').trim();
  if (!raw) return '—';
  return raw.startsWith('#') ? raw : `#${raw}`;
};

const beamPlanHasAnyColor = (beams) =>
  (beams || []).some(
    (b) =>
      String(b?.beamColorId || '').trim() ||
      String(b?.beamColorName || '').trim() ||
      (b?.feeders || []).some(
        (f) => String(f?.colorId || '').trim() || String(f?.colorName || '').trim()
      )
  );

/** Number of feeders configured on a design master (clone compatibility key). */
export const designFeederCount = (design) =>
  Array.isArray(design?.feeders) ? design.feeders.length : 0;

/**
 * Copy beam row count and colour assignments from another order.
 * Source and target designs must have the same feeder count (may be different designs).
 * Quantities (pieces, taka, r.pcs) reset to defaults — user enters those per order.
 */
export const cloneBeamPlanFromOrder = (sourceOrder, targetDesignId, designs) => {
  if (!sourceOrder) return { error: 'Source order not found.' };
  const srcDesignId = String(sourceOrder.designId || '').trim();
  const tgtDesignId = String(targetDesignId || '').trim();
  if (!tgtDesignId) return { error: 'Select a design on this order first.' };

  const targetDesign = (designs || []).find((d) => String(d.id) === tgtDesignId);
  if (!targetDesign) return { error: 'Design data not loaded. Refresh and try again.' };

  const sourceDesign = srcDesignId
    ? (designs || []).find((d) => String(d.id) === srcDesignId)
    : null;
  const targetCount = designFeederCount(targetDesign);
  const sourceCount = sourceDesign
    ? designFeederCount(sourceDesign)
    : Array.isArray(sourceOrder.beamPlan?.beams?.[0]?.feeders)
      ? sourceOrder.beamPlan.beams[0].feeders.length
      : -1;

  if (targetCount <= 0) {
    return { error: 'Current design has no feeders configured.' };
  }
  if (sourceCount !== targetCount) {
    const label =
      sourceOrder.designSampleNo ||
      sourceOrder.designConceptName ||
      sourceOrder.designCategoryName ||
      'another design';
    return {
      error: `Feeder count mismatch — source order (“${label}”) has ${sourceCount} feeder${
        sourceCount === 1 ? '' : 's'
      }; this order’s design has ${targetCount}. Only designs with the same feeder count can be cloned.`,
    };
  }

  if (!sourceOrder.beamPlan?.beams?.length) {
    return { error: 'Source order has no beam plan to clone.' };
  }

  const srcNormalized = normalizeBeamPlanFromApi(sourceOrder.beamPlan, srcDesignId, designs);
  const srcBeams = srcNormalized.beams || [];
  if (!srcBeams.length) return { error: 'Source order has no beam plan to clone.' };
  if (!beamPlanHasAnyColor(srcBeams)) {
    return { error: 'Source order has no beam or feeder colours selected yet.' };
  }

  const isSingleBeam = targetDesign.singleBeam === true;
  const beamsToClone = isSingleBeam ? [srcBeams[0]] : srcBeams;
  const clonedBeams = beamsToClone.map((srcBeam) => ({
    ...DEFAULT_BEAM_ROW_QTY,
    beamColorId: srcBeam.beamColorId ?? '',
    beamColorName: srcBeam.beamColorName || '',
    beamColorCode: srcBeam.beamColorCode || '',
    feeders: mergeBeamFeedersWithDesign(srcBeam.feeders, targetDesign.feeders),
  }));

  return { beamPlan: { beams: clonedBeams } };
};

/** Ledger rows eligible for operator PDF download, in beam/slot order. */
export const collectPdfExportableLedgerRows = (runtimeBeams) => {
  if (!Array.isArray(runtimeBeams)) return [];
  return runtimeBeams
    .filter(isBeamPdfExportable)
    .slice()
    .sort((a, b) => {
      const beamA = Number(a.beamNo ?? a.beam_no) || 0;
      const beamB = Number(b.beamNo ?? b.beam_no) || 0;
      if (beamA !== beamB) return beamA - beamB;
      const slotA = Number(a.machineSlot ?? a.machine_slot) || 1;
      const slotB = Number(b.machineSlot ?? b.machine_slot) || 1;
      return slotA - slotB;
    });
};
